import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Loader2,
  ExternalLink,
  X,
  ArrowLeft,
  ArrowUp,
  Plus,
  MoreHorizontal,
  Pencil,
  Trash2,
  Clock,
  PanelLeft,
} from 'lucide-react';
import { useThemeLogos } from "@/hooks/useThemeLogos";
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

import { useSideRail } from '@/contexts/SideRailContext';
import { useIsMobile } from '@/hooks/use-mobile';
import { useOrganization } from '@/hooks/useOrganization';
import {
  useAskHobbes,
  formatEvidenceDate,
  getBuyingIntentBadgeClassName,
  cleanResponseContent,
} from '@/hooks/useAskHobbes';
import {
  useDeleteAskHobbesChat,
  useRenameAskHobbesChat,
} from '@/hooks/useAskHobbesHistory';
import { prepareResponseContent } from '@/lib/orgAskStream';
import {
  getQualificationBadgeVariant,
  getQualificationBadgeClassName,
  getQualificationBadgeLabel,
} from '@/lib/badgeUtils';
import type { OrgAskEvidenceItem, OrgAskSessionEvidence } from '@/lib/api';

interface AskHobbesChatProps {
  mode?: 'rail' | 'page';
}

const renderEvidenceCard = (item: OrgAskEvidenceItem, index: number) => {
  if (item.type !== 'session') {
    const sessionIds = Array.isArray(item.sessions) ? item.sessions : [];
    return (
      <div
        key={`aggregate-${index}`}
        className="rounded-lg border border-border/80 bg-muted/20 px-3 py-2.5 text-sm text-foreground"
      >
        <p>{item.content}</p>
        {sessionIds.length > 0 && (
          <div className="mt-3 space-y-2">
            <div className="text-[11px] font-medium uppercase text-muted-foreground">
              Related sessions
            </div>
            <div className="flex flex-wrap gap-2 text-xs text-foreground">
              {sessionIds.slice(0, 5).map((sessionId) => (
                <Button key={sessionId} variant="outline" size="sm" asChild>
                  <Link to={`/analytics/sessions?sessionId=${encodeURIComponent(sessionId)}`}>
                    {sessionId.slice(0, 8)}
                  </Link>
                </Button>
              ))}
              {sessionIds.length > 5 && (
                <span className="text-[11px] font-medium text-muted-foreground">
                  +{sessionIds.length - 5} more
                </span>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }

  const sessionItem: OrgAskSessionEvidence = item;
  const sessionLink = `/analytics/sessions?sessionId=${encodeURIComponent(sessionItem.session)}`;
  const startedAt = formatEvidenceDate(sessionItem.started_at || sessionItem.created_at);

  return (
    <div
      key={sessionItem.session}
      className="space-y-3 rounded-lg border border-border/80 bg-muted/20 p-3"
    >
      {/* Header with email and badges */}
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1 space-y-2">
          {sessionItem.email && (
            <div className="truncate text-sm font-medium text-foreground">{sessionItem.email}</div>
          )}
          <p className="text-sm leading-5 text-foreground">{sessionItem.content}</p>
          <div className="flex flex-wrap items-center gap-2">
            {(sessionItem.qualification_status || sessionItem.booked_status) && (
              <Badge
                variant={getQualificationBadgeVariant(sessionItem.qualification_status, sessionItem.booked_status)}
                className={`capitalize ${getQualificationBadgeClassName(sessionItem.qualification_status, sessionItem.booked_status)}`}
              >
                {getQualificationBadgeLabel(sessionItem.qualification_status, sessionItem.booked_status)}
              </Badge>
            )}
            {sessionItem.buying_intent && (
              <Badge
                variant="outline"
                className={`capitalize ${getBuyingIntentBadgeClassName(sessionItem.buying_intent)}`}
              >
                {sessionItem.buying_intent} intent
              </Badge>
            )}
            {sessionItem.sentiment && (
              <Badge variant="outline" className="capitalize">
                {sessionItem.sentiment}
              </Badge>
            )}
            {startedAt && (
              <span className="text-xs text-muted-foreground">{startedAt}</span>
            )}
          </div>
        </div>
        <Button variant="outline" size="sm" className="h-7 shrink-0 px-2 text-xs" asChild>
          <Link to={sessionLink} className="flex items-center gap-2 shrink-0">
            View
            <ExternalLink className="h-3 w-3" />
          </Link>
        </Button>
      </div>

      {/* Topics Discussed */}
      {sessionItem.topics_discussed && sessionItem.topics_discussed.length > 0 && (
        <div>
          <div className="mb-2 text-xs font-medium text-muted-foreground">Topics</div>
          <div className="flex flex-wrap gap-2">
            {sessionItem.topics_discussed.map((topic, idx) => (
              <Badge key={idx} variant="secondary" className="text-xs">
                {topic}
              </Badge>
            ))}
          </div>
        </div>
      )}

      {/* Objections */}
      {sessionItem.objection_types && sessionItem.objection_types.length > 0 && (
        <div>
          <div className="mb-2 text-xs font-medium text-muted-foreground">Objections</div>
          <div className="flex flex-wrap gap-2">
            {sessionItem.objection_types.map((objection, idx) => (
              <Badge key={idx} variant="outline" className="text-xs text-red-700 border-red-200">
                {objection}
              </Badge>
            ))}
          </div>
        </div>
      )}

      {/* Next Steps */}
      {sessionItem.next_steps && sessionItem.next_steps.length > 0 && (
        <div>
          <div className="mb-2 text-xs font-medium text-muted-foreground">Next steps</div>
          <ul className="space-y-1 text-sm text-foreground">
            {sessionItem.next_steps.map((step, idx) => (
              <li key={idx} className="flex items-start gap-2">
                <span className="mt-0.5 text-muted-foreground">•</span>
                <span>{step}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export const AskHobbesChat: React.FC<AskHobbesChatProps> = ({ mode = 'rail' }) => {
  const sideRailContext = useSideRail();
  const {
    entries,
    chats,
    activeChatId,
    isLoadingChats,
    isLoadingActiveChat,
    isSubmitting,
    handleSubmit,
    handleNewChat,
    handleSelectChat,
  } = useAskHobbes();
  const { currentOrganization } = useOrganization();
  const renameChat = useRenameAskHobbesChat(currentOrganization?.id);
  const deleteChat = useDeleteAskHobbesChat(currentOrganization?.id);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [prompt, setPrompt] = useState('');
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const handledPromptRef = useRef<string | null>(null);
  const { favicon: HobbesIcon } = useThemeLogos();
  const isMobile = useIsMobile();

  const isRailMode = mode === 'rail';
  const isPageMode = mode === 'page';
  const isDesktopPage = isPageMode && !isMobile;
  const isHistoryVisible = isRailMode ? sideRailContext.sideRailView === 'history' : isHistoryOpen;
  const showHistoryTakeover = !isDesktopPage && isHistoryVisible;

  const focusComposer = () => {
    window.requestAnimationFrame(() => textareaRef.current?.focus());
  };

  const setHistoryVisible = (open: boolean) => {
    if (isRailMode) {
      sideRailContext.setSideRailView(open ? 'history' : 'chat');
      return;
    }
    setIsHistoryOpen(open);
  };

  const handleOpenFullScreen = () => {
    if (isRailMode && sideRailContext) {
      sideRailContext.closeSideRail();
    }
    navigate('/ask-hobbes');
  };

  const handleBack = () => {
    navigate('/');
  };

  const handleRenameChat = (chatId: string, currentTitle: string) => {
    const nextTitle = window.prompt('Rename chat', currentTitle)?.trim();
    if (!nextTitle || nextTitle === currentTitle) return;
    renameChat.mutate({ chatId, title: nextTitle });
  };

  const handleStartNewChat = () => {
    handleNewChat();
    setHistoryVisible(false);
    focusComposer();
  };

  const handleChooseChat = (chatId: string) => {
    handleSelectChat(chatId);
    setHistoryVisible(false);
  };

  const handleDeleteChat = (chatId: string) => {
    if (!window.confirm('Delete this Ask Hobbes chat?')) return;
    deleteChat.mutate(chatId, {
      onSuccess: () => {
        if (activeChatId === chatId) {
          handleStartNewChat();
        }
      },
    });
  };

  // Handle initial prompt from context (rail mode)
  useEffect(() => {
    if (isRailMode && sideRailContext) {
      const { isOpen, initialPrompt, setInitialPrompt } = sideRailContext;
      if (isOpen && initialPrompt && initialPrompt !== handledPromptRef.current) {
        handledPromptRef.current = initialPrompt;
        setHistoryVisible(false);
        handleSubmit(initialPrompt, false, { newChat: true });
        setInitialPrompt('');
      }
    }
  }, [isRailMode, sideRailContext, handleSubmit]);

  // Handle URL prompt param (page mode)
  useEffect(() => {
    if (isPageMode) {
      const urlPrompt = searchParams.get('prompt');
      if (urlPrompt && urlPrompt !== handledPromptRef.current) {
        handledPromptRef.current = urlPrompt;
        setHistoryVisible(false);
        handleSubmit(urlPrompt, false, { newChat: true });
        // Clear the URL param after handling
        setSearchParams({}, { replace: true });
      }
    }
  }, [isPageMode, searchParams, setSearchParams, handleSubmit]);

  // Scroll to bottom when new entries arrive
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [entries]);

  // Grow the composer with wrapped text while keeping the idle state compact.
  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    textarea.style.height = 'auto';
    textarea.style.height = `${Math.min(textarea.scrollHeight, 160)}px`;
  }, [prompt]);

  const handleFormSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (prompt.trim()) {
      handleSubmit(prompt.trim());
      setPrompt('');
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      if (prompt.trim()) {
        handleSubmit(prompt.trim());
        setPrompt('');
      }
    }
  };

  // Container classes based on mode
  const baseClasses = "flex flex-col overflow-hidden border border-border/80 bg-background text-foreground shadow-sm";
  const containerClasses = isPageMode
    ? `w-full ${isMobile ? "h-full" : "h-[calc(100vh-2rem)]"} rounded-lg ${baseClasses}`
    : `${isMobile ? "h-full w-full" : "h-full w-[420px] rounded-lg"} ${baseClasses}`;

  const historyPanel = (
    <div className="flex h-full min-h-0 flex-col bg-background">
      <div className="flex shrink-0 items-center justify-between gap-2 border-b border-border/60 px-4 py-3">
        <div>
          <div className="text-sm font-medium text-foreground">Chats</div>
        </div>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={handleStartNewChat}
          disabled={isSubmitting}
          className="h-7 gap-1 px-2 text-xs text-muted-foreground hover:text-foreground"
        >
          <Plus className="h-3.5 w-3.5" />
          New
        </Button>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto p-3">
        {isLoadingChats ? (
          <div className="flex items-center gap-2 px-2 py-2 text-xs text-muted-foreground">
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            Loading chats…
          </div>
        ) : chats.length === 0 ? (
          <div className="px-2 py-2 text-xs text-muted-foreground">No chats yet.</div>
        ) : (
          <div className="space-y-1">
            {chats.map((chat) => {
              const isActive = chat.id === activeChatId;
              return (
                <div
                  key={chat.id}
                  className={`group flex items-center gap-1 rounded-md border px-2 py-1.5 ${
                    isActive
                      ? 'border-border bg-muted/60'
                      : 'border-transparent hover:border-border/70 hover:bg-muted/40'
                  }`}
                >
                  <button
                    type="button"
                    onClick={() => handleChooseChat(chat.id)}
                    disabled={isSubmitting && !isActive}
                    className="min-w-0 flex-1 text-left"
                  >
                    <div className="truncate text-xs font-medium text-foreground">{chat.title}</div>
                  </button>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-6 w-6 shrink-0 opacity-70 group-hover:opacity-100">
                        <MoreHorizontal className="h-3.5 w-3.5" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => handleRenameChat(chat.id, chat.title)}>
                        <Pencil className="mr-2 h-3.5 w-3.5" />
                        Rename
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => handleDeleteChat(chat.id)}
                        className="text-destructive focus:text-destructive"
                      >
                        <Trash2 className="mr-2 h-3.5 w-3.5" />
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );

  const content = (
    <>
      {/* Header */}
      {!isDesktopPage && (
      <div className="flex h-11 shrink-0 items-center justify-between border-b border-border/60 px-4">
        <div className="flex min-w-0 items-center gap-2">
          {isPageMode && (
            <Button
              variant="ghost"
              size="icon"
              onClick={handleBack}
              className="h-8 w-8 mr-1"
              title="Back to Dashboard"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
          )}
          <img src={HobbesIcon} alt="Hobbes" className="h-4 w-4 shrink-0" />
          <h2 className="truncate text-sm font-medium text-foreground">Ask Hobbes</h2>
        </div>
        {(!isDesktopPage || isRailMode) && (
          <div className="flex items-center gap-1">
            {!isDesktopPage && (
              <Button
                variant={isHistoryVisible ? "secondary" : "ghost"}
                size="icon"
                onClick={() => setHistoryVisible(!isHistoryVisible)}
                className="h-7 w-7 text-muted-foreground hover:text-foreground"
                title={isHistoryVisible ? "Back to chat" : "Chats"}
                aria-label={isHistoryVisible ? "Back to chat" : "Chats"}
              >
                <Clock className="h-3.5 w-3.5" />
              </Button>
            )}
            {isRailMode && (
              <>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleOpenFullScreen}
              className="h-7 w-7 text-muted-foreground hover:text-foreground"
              title="Open in new tab"
            >
              <ExternalLink className="h-3.5 w-3.5" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => sideRailContext?.closeSideRail()}
              className="h-7 w-7 text-muted-foreground hover:text-foreground"
            >
              <X className="h-3.5 w-3.5" />
            </Button>
              </>
            )}
          </div>
        )}
      </div>
      )}

      {showHistoryTakeover ? (
        historyPanel
      ) : (
        <>
      {/* Chat Messages Area */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4">
        {isLoadingActiveChat && activeChatId ? (
          <div className="flex h-full items-center justify-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading chat…
          </div>
        ) : entries.length === 0 ? (
          activeChatId ? (
            <div className="flex h-full items-end pb-3 text-sm text-muted-foreground">
              This chat has no saved turns yet.
            </div>
          ) : (
            <div className="h-full" />
          )
        ) : (
          <div className="space-y-5">
            {entries.map((entry) => (
              <div key={entry.id} className="space-y-3">
                {/* User message */}
                <div className="flex justify-end">
                  <div className="max-w-[86%] rounded-xl bg-foreground px-3 py-2 text-sm text-background shadow-sm">
                    {entry.prompt}
                  </div>
                </div>

                {/* Hobbes response */}
                <div className="flex justify-start">
                  <div className="max-w-[92%] space-y-3 text-sm">
                    {/* Loading state */}
                    {entry.status === 'loading' && !entry.progress && (
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        <span>Hobbes is thinking...</span>
                      </div>
                    )}

                    {/* Progress state */}
                    {entry.progress && (
                      <div className="flex items-start gap-2 text-xs">
                        <Loader2 className="mt-0.5 h-3.5 w-3.5 animate-spin text-muted-foreground" />
                        <div>
                          <div className="font-medium text-foreground">{entry.progress.headline}</div>
                          {entry.progress.subline && (
                            <div className="mt-1 italic text-muted-foreground">{entry.progress.subline}</div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Error state */}
                    {entry.status === 'error' && (
                      <div className="rounded-md border border-destructive/50 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                        {entry.errorMessage ?? 'Something went wrong.'}
                      </div>
                    )}

                    {/* Success state */}
                    {entry.status === 'success' && (
                      <div className="prose prose-sm prose-zinc max-w-none leading-relaxed dark:prose-invert prose-p:my-2 prose-ul:my-2 prose-li:my-0.5">
                        <ReactMarkdown
                          remarkPlugins={[remarkGfm]}
                          components={{
                            a: ({ node, href, ...props }) =>
                              href && href.startsWith('/') ? (
                                <Link to={href} {...props} />
                              ) : (
                                <a href={href} target="_blank" rel="noopener noreferrer" {...props} />
                              ),
                          }}
                        >
                          {cleanResponseContent(prepareResponseContent(
                            entry.response?.content,
                            (entry.response?.evidence?.length ?? 0) > 0
                          )) || 'No answer returned.'}
                        </ReactMarkdown>
                      </div>
                    )}

                    {/* Evidence cards */}
                    {entry.status === 'success' && (entry.response?.evidence?.length ?? 0) > 0 && (
                      <div className="space-y-2 border-t border-border/80 pt-3">
                        <div className="text-[11px] font-medium uppercase text-muted-foreground">
                          Evidence
                        </div>
                        {entry.response?.evidence?.map((item, index) => renderEvidenceCard(item, index))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Chat Input */}
      <div className="shrink-0 border-t border-border/80 p-3">
        <form onSubmit={handleFormSubmit} className="flex items-end gap-3 rounded-xl border border-border/80 bg-background px-3 py-2 shadow-sm">
          <Textarea
            ref={textareaRef}
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Ask Hobbes about sessions, people, accounts..."
            disabled={isSubmitting}
            rows={1}
            className="max-h-40 min-h-9 flex-1 resize-none overflow-y-auto border-0 bg-transparent px-0 py-2 text-sm shadow-none focus-visible:ring-0"
          />
          <Button
            type="submit"
            size="icon"
            disabled={isSubmitting || !prompt.trim()}
            className="mb-1 h-7 w-7 shrink-0 rounded-full"
            aria-label="Send message"
          >
            {isSubmitting && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            {!isSubmitting && <ArrowUp className="h-3.5 w-3.5" />}
          </Button>
        </form>
      </div>
        </>
      )}
    </>
  );

  // In page mode, render without animation
  if (isPageMode) {
    if (isDesktopPage) {
      return (
        <div className={containerClasses}>
          <div className="flex h-full min-h-0 w-full">
            {isSidebarCollapsed ? (
              <aside className="flex h-full w-12 shrink-0 flex-col items-center gap-1 border-r border-border/60 bg-background py-3">
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => setIsSidebarCollapsed(false)}
                  className="h-8 w-8 text-muted-foreground hover:text-foreground"
                  title="Expand chats"
                  aria-label="Expand chats"
                >
                  <PanelLeft className="h-4 w-4" />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={handleStartNewChat}
                  disabled={isSubmitting}
                  className="h-8 w-8 text-muted-foreground hover:text-foreground"
                  title="New chat"
                  aria-label="New chat"
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </aside>
            ) : (
              <aside className="flex h-full w-72 shrink-0 flex-col border-r border-border/60 bg-background">
                <div className="flex h-14 shrink-0 items-center justify-between gap-2 border-b border-border/60 px-4">
                  <div className="truncate text-sm font-medium text-foreground">Chats</div>
                  <div className="flex shrink-0 items-center gap-1">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={handleStartNewChat}
                      disabled={isSubmitting}
                      className="h-7 gap-1 px-2 text-xs text-muted-foreground hover:text-foreground"
                    >
                      <Plus className="h-3.5 w-3.5" />
                      New
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => setIsSidebarCollapsed(true)}
                      className="h-7 w-7 text-muted-foreground hover:text-foreground"
                      title="Collapse chats"
                      aria-label="Collapse chats"
                    >
                      <PanelLeft className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
                <div className="min-h-0 flex-1 overflow-y-auto p-3">
                  {isLoadingChats ? (
                    <div className="flex items-center gap-2 px-2 py-2 text-xs text-muted-foreground">
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      Loading chats…
                    </div>
                  ) : chats.length === 0 ? (
                    <div className="px-2 py-2 text-xs text-muted-foreground">No chats yet.</div>
                  ) : (
                    <div className="space-y-1">
                      {chats.map((chat) => {
                        const isActive = chat.id === activeChatId;
                        return (
                          <div
                            key={chat.id}
                            className={`group flex items-center gap-1 rounded-md px-2 py-1.5 ${
                              isActive
                                ? 'bg-muted/70'
                                : 'hover:bg-muted/45'
                            }`}
                          >
                            <button
                              type="button"
                              onClick={() => handleChooseChat(chat.id)}
                              disabled={isSubmitting && !isActive}
                            className="min-w-0 flex-1 text-left"
                          >
                            <div className="truncate text-xs font-medium text-foreground">{chat.title}</div>
                          </button>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon" className="h-6 w-6 shrink-0 opacity-70 group-hover:opacity-100">
                                  <MoreHorizontal className="h-3.5 w-3.5" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => handleRenameChat(chat.id, chat.title)}>
                                  <Pencil className="mr-2 h-3.5 w-3.5" />
                                  Rename
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  onClick={() => handleDeleteChat(chat.id)}
                                  className="text-destructive focus:text-destructive"
                                >
                                  <Trash2 className="mr-2 h-3.5 w-3.5" />
                                  Delete
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </aside>
            )}
            <div className="flex min-w-0 flex-1 flex-col">
              {content}
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className={containerClasses}>
        {content}
      </div>
    );
  }

  // In rail mode, render with slide animation
  return (
    <motion.div
      key="ask-hobbes-chat"
      initial={{ x: "100%", opacity: 0 }}
      animate={{
        x: 0,
        opacity: 1,
        transition: {
          type: "tween",
          duration: 0.5,
          ease: [0.25, 0.1, 0.25, 1]
        }
      }}
      exit={{
        x: "100%",
        opacity: 0,
        transition: {
          type: "tween",
          duration: 0.3,
          ease: [0.25, 0.1, 0.25, 1]
        }
      }}
      className={containerClasses}
    >
      {content}
    </motion.div>
  );
};
